/* istanbul ignore file */
import { createApi } from "@reduxjs/toolkit/query/react";
import baseQuery from "../utils/baseQuery";
import { showNotifier, setGlobalLoader } from "../../store/globalSlice";
import store from "../../store";
import { setSearchedNoteType } from "../../store/notesSlice";
import config from "../../config";

// Get access token directly from redux store.
const getAuthHeaders = () => ({
  Authorization: `Bearer ${store.getState()?.global?.authState?.id_token}`,
});

export const notesApi = createApi({
  reducerPath: "notes/api",
  tagTypes: ["Notes"],
  baseQuery: baseQuery({ baseUrl: config.NotesBaseUrl }),
  endpoints: (builder) => ({
    getNotesForPlanogram: builder.query({
      providesTags: ["Planogram Notes"],
      query: ({ noteType, storeId }) => ({
        url: `${config.GetNotesListForStore}`,
        method: "post",
        headers: getAuthHeaders(),
        data: {
          storeId: storeId,
          notesType: noteType,
        },
      }),
    }),
    getNotes: builder.query({
      providesTags: ["Notes"],
      query: ({ noteType }) => ({
        url: config.GetNotesList,
        method: "post",
        headers: getAuthHeaders(),
        data: {
          NotesType: noteType,
        },
      }),
      async onQueryStarted(_details, { dispatch, queryFulfilled }) {
        // Note: showing global loader only for Admin Manage Notes page search action
        // Not needed for other places like planogram page where we call this API twice
        try {
          if (_details?.type === "search") {
            dispatch(setGlobalLoader(true));
          }
          const { data } = await queryFulfilled;
          dispatch(setGlobalLoader(false));
          dispatch(setSearchedNoteType());
          if (!data.length) {
            dispatch(
              showNotifier({
                message: `No details found`,
                severity: "info",
              })
            );
          }
        } catch {
          dispatch(setGlobalLoader(false));
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      },
    }),
    updateNote: builder.mutation({
      query: (details) => ({
        url: `${config.EditNote}`,
        method: "POST",
        headers: getAuthHeaders(),
        data: {
          ...details,
        },
      }),
      invalidatesTags: ["Notes"],
      async onQueryStarted(_details, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            showNotifier({
              message: `Note updated successfully`,
              severity: "success",
            })
          );
        } catch {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      },
    }),
    deleteNote: builder.mutation({
      query: ({ id, userName }) => ({
        url: config.DeleteNote,
        method: "POST",
        headers: getAuthHeaders(),
        data: {
          notesId: id,
          updatedBy: userName,
        },
      }),
      invalidatesTags: ["Notes"],
      async onQueryStarted(_details, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            showNotifier({
              message: `Note deleted successfully`,
              severity: "success",
            })
          );
        } catch {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      },
    }),
    addNote: builder.mutation({
      query: (details) => ({
        url: `${config.AddNote}`,
        method: "POST",
        headers: getAuthHeaders(),
        data: details,
      }),
      invalidatesTags: ["Notes", "Planogram Notes"],
      async onQueryStarted(_details, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          showNotifier({
            message: `Note added successfully`,
            severity: "success",
          })
        );
      },
    }),
  }),
});

export const {
  useGetNotesForPlanogramQuery,
  useLazyGetNotesForPlanogramQuery,
  useGetNotesQuery,
  useLazyGetNotesQuery,
  useAddNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} = notesApi;
